// Pure, dependency-free elevation-profile math for the 3D flyover, shared by the live web preview
// (FlyoverElevationProfile.vue) and the headless share-video renderer (lib/flyover_renderer/entry.ts)
// so all three platforms — web, iOS, Android — draw the SAME silhouette, steepness colour ramp and
// grade readout. No turf/mapbox/DOM imports, so it is unit-testable and cheap to run per frame.
//
// The apps deliberately ported the same computeProfile / steepColor from the web renderer; keeping the
// definitions here (rather than duplicated in each caller) is what stops the ramp drifting across them.

// SVG viewBox the profile is drawn into: 1000 wide (x = progress * 1000), PROFILE_VIEWBOX_H tall.
// The silhouette baseline sits at PROFILE_BASELINE_Y and the curve rises PROFILE_CURVE_H above it, so
// the highest smoothed point lands at (PROFILE_BASELINE_Y - PROFILE_CURVE_H) and the lowest at the
// baseline. These match the renderer's original 116 / 100 / 120 constants exactly.
export const PROFILE_VIEWBOX_W = 1000;
export const PROFILE_VIEWBOX_H = 120;
export const PROFILE_BASELINE_Y = 116;
export const PROFILE_CURVE_H = 100;

export interface ProfilePoint {
  x: number;
  y: number;
}

export interface FlyoverProfile {
  /** Points in the 1000 x PROFILE_VIEWBOX_H viewBox, one per route vertex. */
  points: ProfilePoint[];
  /** Cumulative distance (km) at each route vertex. */
  cumKm: number[];
  /** Smoothed elevation (m) at each route vertex. */
  elevSmooth: number[];
  /** Total route length (km); never 0 (floored to 1) so callers can divide safely. */
  totalKm: number;
  /** Progress fraction (0..1) of the smoothed high point. */
  summitFrac: number;
  /** Max / min RAW elevation (m) over the route. */
  maxElevM: number;
  minElevM: number;
}

const EARTH_RADIUS_KM = 6371;

/** Great-circle distance (km) between two [lng, lat] coordinates. */
function haversineKm(a: number[], b: number[]): number {
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLon = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Box moving average with a +/- `win` window (so the effective width is up to 2*win+1). GPS elevation
 * is jittery; this cleans the silhouette without shifting the peak. `win <= 1` returns a copy.
 */
export function movingAverage(arr: number[], win: number): number[] {
  if (win <= 1) return arr.slice();
  const out: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    let s = 0;
    let n = 0;
    for (let j = Math.max(0, i - win); j <= Math.min(arr.length - 1, i + win); j++) {
      s += arr[j];
      n++;
    }
    out.push(s / n);
  }
  return out;
}

/**
 * Build the elevation-profile geometry for a route of `[lng, lat, elev?]` vertices. Returns viewBox
 * points plus the cumulative-distance / smoothed-elevation arrays callers reuse for grade + readouts.
 */
export function computeProfile(coords: number[][]): FlyoverProfile {
  const empty: FlyoverProfile = {
    points: [],
    cumKm: [0],
    elevSmooth: [],
    totalKm: 1,
    summitFrac: 0,
    maxElevM: 0,
    minElevM: 0,
  };
  if (!coords || coords.length === 0) return empty;

  const cumKm = [0];
  for (let i = 1; i < coords.length; i++) {
    cumKm.push(cumKm[i - 1] + haversineKm(coords[i - 1], coords[i]));
  }
  const totalKm = cumKm[cumKm.length - 1] || 1;

  const elevs = coords.map((c) => c[2] || 0);
  // Light smoothing so the silhouette is clean; window scales with vertex count (matches the apps).
  const elevSmooth = movingAverage(elevs, Math.max(1, Math.round(coords.length / 50)));
  const maxElevM = Math.max(...elevs);
  const minElevM = Math.min(...elevs);

  const minS = Math.min(...elevSmooth);
  const maxS = Math.max(...elevSmooth);
  const span = maxS - minS || 1;
  const points = coords.map((c, i) => ({
    x: (cumKm[i] / totalKm) * PROFILE_VIEWBOX_W,
    y: PROFILE_BASELINE_Y - ((elevSmooth[i] - minS) / span) * PROFILE_CURVE_H,
  }));

  let mi = 0;
  for (let i = 1; i < elevSmooth.length; i++) if (elevSmooth[i] > elevSmooth[mi]) mi = i;
  const summitFrac = totalKm > 0 ? cumKm[mi] / totalKm : 0;

  return { points, cumKm, elevSmooth, totalKm, summitFrac, maxElevM, minElevM };
}

/** Linear interpolation between two `#rrggbb` colours, returning an `rgb(...)` string. */
function lerpHex(a: string, b: string, t: number): string {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

// Continuous steepness colour ramp: gentle green -> yellow -> orange -> steep red, keyed on |grade %|
// and capped at 18%. Kept identical to the renderer + apps so the profile reads the same everywhere.
const STEEP_STOPS: [number, string][] = [
  [0, "#5fbf6a"],
  [6, "#FFD400"],
  [12, "#FF9A3C"],
  [18, "#FF4D4D"],
];

export function steepColor(gradePct: number): string {
  const g = Math.min(Math.abs(gradePct), 18);
  for (let i = 1; i < STEEP_STOPS.length; i++) {
    if (g <= STEEP_STOPS[i][0]) {
      const [g0, c0] = STEEP_STOPS[i - 1];
      const [g1, c1] = STEEP_STOPS[i];
      return lerpHex(c0, c1, (g - g0) / (g1 - g0));
    }
  }
  return STEEP_STOPS[STEEP_STOPS.length - 1][1];
}

/**
 * Interpolate the profile's viewBox y at a horizontal progress fraction `frac` (0..1), walking the
 * point polyline. Used to place the head dot on the curve. Returns the baseline for an empty profile.
 */
export function profileYAt(points: ProfilePoint[], frac: number): number {
  if (points.length === 0) return PROFILE_BASELINE_Y;
  const x = frac * PROFILE_VIEWBOX_W;
  for (let i = 1; i < points.length; i++) {
    if (points[i].x >= x) {
      const a = points[i - 1];
      const b = points[i];
      const f = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x);
      return a.y + (b.y - a.y) * f;
    }
  }
  return points[points.length - 1].y;
}

/**
 * Local grade (%) at a distance along the route, measured over a small +/- window. Positive = uphill,
 * negative = downhill. `elevAt` supplies interpolated elevation (m) at a distance (km) — pass
 * getElevationAtDistanceKm bound to the route so this file stays dependency-free.
 */
export function gradeAtDistance(
  distKm: number,
  totalKm: number,
  elevAt: (distKm: number) => number,
  windowKm = 0.15
): number {
  const lo = Math.max(0, distKm - windowKm);
  const hi = Math.min(totalKm, distKm + windowKm);
  const e1 = elevAt(lo);
  const e2 = elevAt(hi);
  const horizM = Math.max((hi - lo) * 1000, 1);
  return ((e2 - e1) / horizM) * 100;
}

/** Format a grade percentage the way the apps do: rounded, always signed (e.g. "+7%", "-3%", "0%"). */
export function formatGrade(gradePct: number): string {
  const rounded = Math.round(gradePct);
  if (rounded > 0) return `+${rounded}%`;
  return `${rounded}%`;
}
